/**
 * React hooks for offline functionality
 */

import { useState, useEffect, useCallback } from 'react'
import {
  offlineSyncManager,
  type SyncStatus,
} from '@/services/offlineSync'
import {
  savePendingVisitNote,
  getPendingVisitNotes,
  isIndexedDBAvailable,
  type PendingVisitNote,
} from '@/lib/storage/offlineDB'
import type { UserRole } from '@/types'

// Hook for online/offline status
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

// Hook for sync status
export function useSyncStatus(): SyncStatus & { refresh: () => Promise<void> } {
  const [status, setStatus] = useState<SyncStatus>(offlineSyncManager.getStatus())

  useEffect(() => {
    // Subscribe to status changes
    const unsubscribe = offlineSyncManager.addEventListener((event) => {
      if (event.type === 'status-change' && event.data?.status) {
        setStatus(event.data.status)
      }
    })

    // Get initial pending count
    offlineSyncManager.getPendingCount()

    return unsubscribe
  }, [])

  const refresh = useCallback(async () => {
    await offlineSyncManager.getPendingCount()
    setStatus(offlineSyncManager.getStatus())
  }, [])

  return { ...status, refresh }
}

// Hook for managing pending visit notes
export function usePendingVisitNotes() {
  const [pendingNotes, setPendingNotes] = useState<PendingVisitNote[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadPendingNotes = useCallback(async () => {
    try {
      if (!isIndexedDBAvailable()) {
        setPendingNotes([])
        return
      }
      const notes = await getPendingVisitNotes()
      setPendingNotes(notes)
    } catch (error) {
      console.error('Failed to load pending notes:', error)
      setPendingNotes([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPendingNotes()

    // Refresh when sync completes
    const unsubscribe = offlineSyncManager.addEventListener((event) => {
      if (event.type === 'sync-complete' || event.type === 'status-change') {
        loadPendingNotes()
      }
    })

    return unsubscribe
  }, [loadPendingNotes])

  return {
    pendingNotes,
    isLoading,
    refresh: loadPendingNotes,
    count: pendingNotes.length,
  }
}

// Hook for offline-capable visit note submission
export function useOfflineVisitNote() {
  const isOnline = useOnlineStatus()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMode, setSubmitMode] = useState<'online' | 'offline' | null>(null)

  const submitVisitNote = useCallback(
    async (
      noteData: Omit<PendingVisitNote['data'], 'visitId'> & { visitId: string },
      options: {
        onSuccess?: (mode: 'online' | 'offline') => void
        onError?: (error: Error) => void
        forceOffline?: boolean
      } = {}
    ) => {
      const { onSuccess, onError, forceOffline = false } = options
      setIsSubmitting(true)
      setSubmitMode(null)

      try {
        // Determine submission mode
        const shouldUseOffline = forceOffline || !isOnline

        if (shouldUseOffline) {
          // Check IndexedDB availability
          if (!isIndexedDBAvailable()) {
            throw new Error('Offline storage not available on this device')
          }

          // Save to IndexedDB for later sync
          const pendingNote: PendingVisitNote = {
            id: `pending_${noteData.visitId}_${Date.now()}`,
            type: 'visit_note',
            createdAt: Date.now(),
            retryCount: 0,
            data: noteData,
          }

          await savePendingVisitNote(pendingNote)
          setSubmitMode('offline')
          onSuccess?.('offline')

          console.log('[useOfflineVisitNote] Saved visit note offline:', pendingNote.id)
        } else {
          // Import and call the online submission
          const { createVisitNote } = await import('@/services/visitNotes')

          await createVisitNote({
            data: {
              visitId: noteData.visitId,
              clientId: noteData.clientId,
              summary: noteData.summary,
              clientCondition: noteData.clientCondition as 'stable' | 'improved' | 'declined' | 'unchanged',
              clientMood: noteData.clientMood,
              activitiesCompleted: noteData.activitiesCompleted,
              vitalSigns: noteData.vitalSigns as Record<string, number | string | undefined> | undefined,
              tasksCompleted: noteData.tasksCompleted.map(t => ({
                taskId: t.taskId,
                description: t.description,
                completed: t.completed,
                notes: t.notes,
              })),
              photos: noteData.photos?.map(p => ({
                id: p.id,
                type: p.type as 'wound' | 'medication' | 'safety' | 'other',
                dataUrl: p.dataUrl,
                caption: p.caption,
                timestamp: new Date(),
              })),
              signature: noteData.signature,
              aiGenerated: noteData.aiGenerated,
              aiTranscript: noteData.aiTranscript,
            },
            agencyId: noteData.agencyId,
            caregiverId: noteData.caregiverId,
            userId: noteData.userId,
            userEmail: noteData.userEmail,
            userRole: noteData.userRole as UserRole,
          })

          setSubmitMode('online')
          onSuccess?.('online')
        }
      } catch (error) {
        console.error('[useOfflineVisitNote] Submission error:', error)

        // If online submission fails, try offline as fallback
        if (isOnline && isIndexedDBAvailable()) {
          try {
            const pendingNote: PendingVisitNote = {
              id: `pending_${noteData.visitId}_${Date.now()}`,
              type: 'visit_note',
              createdAt: Date.now(),
              retryCount: 0,
              data: noteData,
            }

            await savePendingVisitNote(pendingNote)
            setSubmitMode('offline')
            onSuccess?.('offline')

            console.log('[useOfflineVisitNote] Saved to offline after online failure:', pendingNote.id)
            return
          } catch (fallbackError) {
            console.error('[useOfflineVisitNote] Offline fallback failed:', fallbackError)
          }
        }

        onError?.(error instanceof Error ? error : new Error('Submission failed'))
        throw error
      } finally {
        setIsSubmitting(false)
      }
    },
    [isOnline]
  )

  return {
    submitVisitNote,
    isSubmitting,
    submitMode,
    isOnline,
    canUseOffline: isIndexedDBAvailable(),
  }
}

// Hook for triggering manual sync
export function useSync() {
  const [isSyncing, setIsSyncing] = useState(false)
  const isOnline = useOnlineStatus()

  const sync = useCallback(async () => {
    if (!isOnline) {
      throw new Error('Cannot sync while offline')
    }

    setIsSyncing(true)
    try {
      await offlineSyncManager.forceSync()
    } finally {
      setIsSyncing(false)
    }
  }, [isOnline])

  return {
    sync,
    isSyncing,
    canSync: isOnline,
  }
}
